// InfoSharing.tsx - API 연동된 정보공유 게시판 컴포넌트
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import CommunityLayout from '../../../components/community/CommunityLayout';
import NoticeCard from '../../../components/common/NoticeCard';
import LoginModal from '../../../components/modals/LoginModal';
import { getPostList, PostListItem, ApiResponse, PostListResponse } from '../../../services/postApi';
import { searchInfoSharingPosts } from '../../../services/searchApi';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import type { PostSearchItem } from '../../../services/searchApi';

// HTML 태그 제거 함수
const stripHtml = (html: string): string => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

const InfoSharing: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const [postList, setPostList] = useState<PostListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  
  // Redux 상태에서 로그인 정보 가져오기
  const { token } = useSelector((state: RootState) => state.auth);
  
  // 로그인 모달 상태
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // 로그인 후 이동할 게시글 ID 저장
  const [pendingPostId, setPendingPostId] = useState<number | null>(null);
  
  // 검색 관련 상태
  const [searchResults, setSearchResults] = useState<PostSearchItem[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  
  // 게시글 목록 조회
  const fetchPosts = useCallback(async (pageNum: number = 0, reset: boolean = false) => {
    /// console.log('게시글 목록 조회 API 요청 데이터:', { pageNum, reset, loading });
    if (loading) return;
    
    setLoading(true);
    try {
      // /// console.log('API 요청 시작 - board: information');
      const response: ApiResponse<PostListResponse> = await getPostList(
        'information',
        pageNum,
        10
      );

      /// console.log('정보 공유 API 응답:', {response});
      if (response.data) {
        const newPosts = response.data.content;
        // /// console.log('받은 게시글 수:', newPosts.length);
        setPostList(reset ? newPosts : prev => [...prev, ...newPosts]);
        setHasMorePosts(!response.data.last);
      }
    } catch (error: any) {
      /// console.error('게시글 목록 조회 실패:', error);
      if (error.message === 'Unauthorized') {
        navigate('/login');
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    if (!isSearchMode) {
      fetchPosts(0, true);
    }
  }, [fetchPosts, isSearchMode]);

  // URL에서 검색 키워드 확인 및 검색 실행
  useEffect(() => {
    const keyword = searchParams.get('keyword');
    if (keyword && keyword.trim()) {
      setSearchValue(keyword);
      setIsSearchMode(true);
      performSearch(keyword);
    } else {
      setIsSearchMode(false);
      setSearchResults([]);
    }
  }, [searchParams]);

  // 검색 실행 함수
  const performSearch = async (keyword: string) => {
    if (!keyword.trim()) return;

    setSearchLoading(true);
    setSearchError(null);

    try {
      const results = await searchInfoSharingPosts(keyword);
      setSearchResults(results);
    } catch (error) {
      /// console.error('정보공유 검색 오류:', error);
      setSearchError('검색 중 오류가 발생했습니다.');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // 더 많은 데이터 로드
  const loadMorePosts = useCallback(() => {
    if (hasMorePosts && !loading && !isSearchMode) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPosts(nextPage, false);
    }
  }, [hasMorePosts, loading, isSearchMode, page, fetchPosts]);

  // 무한스크롤 적용
  useInfiniteScroll({
    hasMore: hasMorePosts && !isSearchMode,
    isLoading: loading,
    onLoadMore: loadMorePosts,
    threshold: 200
  });

  // 게시글 클릭 핸들러
  const handlePostClick = (postId: number) => {
    // /// console.log('=== 게시글 클릭 ===');
    // /// console.log('isAuthenticated:', isAuthenticated);
    // /// console.log('redux token:', token);
    // /// console.log('localStorage access_token:', localStorage.getItem('access_token'));
    
    // Redux와 localStorage 둘 다 확인
    const reduxToken = token;
    const localToken = localStorage.getItem('access_token');
    const finalToken = reduxToken || localToken;
    
    // /// console.log('토큰 상태:', {
    //   reduxToken: reduxToken ? 'exists' : 'null',
    //   localToken: localToken ? 'exists' : 'null',
    //   finalToken: finalToken ? 'exists' : 'null'
    // });
    
    // 토큰이 없으면 로그인 모달 띄우기
    if (!finalToken) {
      // /// console.log('토큰이 없습니다. 로그인 모달을 띄웁니다.');
      setPendingPostId(postId); // 로그인 후 이동할 게시글 ID 저장
      setShowLoginModal(true);
      return;
    }
    
    // 토큰이 있으면 게시글 페이지로 이동
    // /// console.log('로그인되어 있습니다. 게시글 페이지로 이동합니다.');
    navigate(`/community/post/${postId}`);
  };

  // 로그인 성공 후 핸들러
  const handleLoginSuccess = () => {
    // /// console.log('로그인 성공! 모달을 닫습니다.');
    setShowLoginModal(false);
    
    // 로그인 후 대기 중인 게시글로 자동 이동
    if (pendingPostId) {
      // /// console.log(`로그인 완료. 게시글 ${pendingPostId}로 이동합니다.`);
      navigate(`/community/post/${pendingPostId}`);
      setPendingPostId(null); // 상태 초기화
    }
  };

  // 로그인 모달 닫기 핸들러
  const handleLoginModalClose = () => {
    setShowLoginModal(false);
    setPendingPostId(null); // 모달을 닫을 때도 상태 초기화
  };
  
  // 공지사항 필터링 (pinned=true인 게시글)
  const pinnedPosts = postList.filter(post => post.pinned);
  const regularPosts = postList.filter(post => !post.pinned);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const searchTerm = (e.target as HTMLInputElement).value.trim();
      if (searchTerm) {
        // URL 파라미터 변경하여 검색 실행
        navigate(`/community/info-sharing?keyword=${encodeURIComponent(searchTerm)}`);
      } else {
        // 검색어가 비어있으면 기본 페이지로 이동
        navigate('/community/info-sharing');
      }
    }
  };

  return (
    <CommunityLayout>
      <Container>
        <MainContainer>
          {/* 페이지 헤더 */}
          <PageHeader>
            <PageTitle>정보 공유</PageTitle>
          </PageHeader>

          {/* 브레드크럼과 검색바 */}
          <NavigationBar>
            <BreadcrumbContainer>
              <BreadcrumbItem>홈</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>커뮤니티</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>정보 공유</BreadcrumbItem>
            </BreadcrumbContainer>
            
            <SearchContainer>
              <SearchBox>
                <SearchIcon>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path 
                      d="M14.87 14.87L10.4 10.4M12 6.5C12 9.54 9.54 12 6.5 12C3.46 12 1 9.54 1 6.5C1 3.46 3.46 1 6.5 1C9.54 1 12 3.46 12 6.5Z" 
                      stroke="rgba(55, 56, 60, 0.28)" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </SearchIcon>
                <SearchInput 
                  type="text" 
                  placeholder="검색어를 입력해주세요."
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyUp={handleSearchSubmit}
                />
              </SearchBox>
            </SearchContainer>
          </NavigationBar>

          {/* 상단고정 공지사항 */}
          {pinnedPosts.length > 0 && (
            <NoticeSection>
              {pinnedPosts.map(notice => (
                <NoticeCard 
                  key={notice.post_id}
                  title={notice.title}
                  onClick={() => handlePostClick(notice.post_id)}
                />
              ))}
            </NoticeSection>
          )}

          {/* 게시글 목록 */}
          <InfoList>
            {isSearchMode ? (
              // 검색 모드
              <>
                {searchLoading ? (
                  <LoadingState>검색 중...</LoadingState>
                ) : searchError ? (
                  <EmptyState>{searchError}</EmptyState>
                ) : searchResults.length === 0 ? (
                  <EmptyState>'{searchValue}'에 대한 검색 결과가 없습니다.</EmptyState>
                ) : (
                  searchResults.map((post, index) => (
                    <React.Fragment key={post.id}>
                      <InfoCard onClick={() => handlePostClick(post.id)}>
                        <InfoContent>
                          <InfoHeader>
                            <InfoTitle>{post.title}</InfoTitle>
                            <InfoDescription>
                              {stripHtml(post.htmlcontent).length > 100 
                                ? stripHtml(post.htmlcontent).substring(0, 100) + '...' 
                                : stripHtml(post.htmlcontent)}
                            </InfoDescription>
                          </InfoHeader>
                          
                          <InfoFooter>
                            <InfoAuthorInfo>
                              <AuthorAvatar>
                                <img src={post.writerProfileImageUrl || "/images/default-avatar.png"} alt="작성자" />
                              </AuthorAvatar>
                              <AuthorName>{post.writerName}</AuthorName>
                              <Divider />
                              <InfoDate>{new Date(post.postDate).toLocaleString('ko-KR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                              })}</InfoDate>
                            </InfoAuthorInfo>
                            
                            <InfoStats>
                              <StatItem>댓글 -</StatItem>
                              <Divider />
                              <StatItem>조회수 -</StatItem>
                            </InfoStats>
                          </InfoFooter>
                        </InfoContent>
                      </InfoCard>
                      
                      {index < searchResults.length - 1 && <InfoDivider />}
                    </React.Fragment>
                  ))
                )}
              </>
            ) : (
              // 기본 모드
              <>
                {loading ? (
                  <LoadingState>
                    게시글을 불러오는 중...
                  </LoadingState>
                ) : regularPosts.length === 0 ? (
                  <EmptyState>
                    작성된 게시글이 없습니다.
                  </EmptyState>
                ) : (
                  regularPosts.map((post: PostListItem, index: number) => (
                    <React.Fragment key={post.post_id}>
                      <InfoCard onClick={() => handlePostClick(post.post_id)}>
                        <InfoContent>
                          <InfoHeader>
                            <InfoTitle>{post.title}</InfoTitle>
                            <InfoDescription>
                              {post.content.replace(/<[^>]*>/g, '').length > 100 
                                ? post.content.replace(/<[^>]*>/g, '').substring(0, 100) + '...' 
                                : post.content.replace(/<[^>]*>/g, '')}
                            </InfoDescription>
                          </InfoHeader>
                          
                          <InfoFooter>
                            <InfoAuthorInfo>
                              <AuthorAvatar>
                                <img src={post.writer_profile_image_url || "/images/default-avatar.png"} alt="작성자" />
                              </AuthorAvatar>
                              <AuthorName>{post.writer_name}</AuthorName>
                              <Divider />
                              <InfoDate>{new Date(post.created_at).toLocaleString('ko-KR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                              })}</InfoDate>
                            </InfoAuthorInfo>
                            
                            <InfoStats>
                              <StatItem>댓글 {post.reply_cnt}</StatItem>
                              <Divider />
                              <StatItem>조회수 {post.view_cnt}</StatItem>
                            </InfoStats>
                          </InfoFooter>
                        </InfoContent>
                      </InfoCard>
                      
                      {index < regularPosts.length - 1 && <InfoDivider />}
                    </React.Fragment>
                  ))
                )}
                
                {/* 무한스크롤 로딩 표시 */}
                {loading && regularPosts.length > 0 && !isSearchMode && (
                  <LoadingState>추가 게시글을 불러오는 중...</LoadingState>
                )}
              </>
            )}
          </InfoList>
        </MainContainer>
        
        {/* 로그인 모달 */}
        <LoginModal
          isOpen={showLoginModal}
          onClose={handleLoginModalClose}
          onLoginSuccess={handleLoginSuccess}
        />
      </Container>
    </CommunityLayout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 894px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 60px;
`;

const PageTitle = styled.h1`
  color: #171719;
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  margin: 0;
`;

const NavigationBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const BreadcrumbItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const ChevronIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchContainer = styled.div`
  width: 282px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchBox = styled.div`
  width: 100%;
  padding: 8px;
  background: white;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  outline: 1px solid rgba(112, 115, 124, 0.22);
  outline-offset: -1px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const SearchIcon = styled.div`
  width: 16px;
  height: 16px;
  padding: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: #171719;
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
`;

const NoticeSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: -32px;
`;

const InfoList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
`;

const InfoCard = styled.div`
  width: 100%;
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  cursor: pointer;
`;

const InfoContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InfoHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoTitle = styled.h3`
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
  margin: 0;
`;

const InfoDescription = styled.p`
  color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 21.99px;
  letter-spacing: 0.20px;
  word-wrap: break-word;
  margin: 0;
`;

const InfoFooter = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InfoAuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AuthorAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorName = styled.span`
  color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
  word-wrap: break-word;
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(112, 115, 124, 0.16);
`;

const InfoDate = styled.span`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
  word-wrap: break-word;
`;

const InfoStats = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatItem = styled.span`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
  word-wrap: break-word;
`;

const InfoDivider = styled.div`
  width: 100%;
  height: 1px;
  background: var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
  margin: 0;
`;

const LoadMoreContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 32px;
`;

const LoadMoreButton = styled.button<{ disabled: boolean }>`
  padding: 12px 24px;
  background: ${props => props.disabled ? '#f5f5f5' : '#448181'};
  color: ${props => props.disabled ? '#999' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s ease;

  &:hover {
    background: ${props => props.disabled ? '#f5f5f5' : '#3a6b6b'};
  }
`;

export default InfoSharing;
